import { useParams, Link } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Calendar, Tag, Wrench, BarChart3, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ImageWithFallback } from '@/components/figma/ImageWithFallback'
import { useProject, useCategory } from '@/hooks/useProjects'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { PageLayout } from '@/components/PageLayout'
import { Meta } from '@/components/MetaManager'
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData'

function ProjectDetailSkeleton() {
  return (
    <PageLayout>
      <div className="py-20">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <Skeleton className="h-[400px] rounded-3xl mb-8" />
            <Skeleton className="h-8 w-64 mb-4" />
            <Skeleton className="h-6 w-96 mb-8" />
            <div className="grid md:grid-cols-2 gap-8">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default function ProjectDetail() {
  const { category, projectId } = useParams({ from: '/$category/$projectId' }) as { category?: string; projectId?: string }
  const { t, i18n } = useTranslation()
  
  const { data: project, isLoading: projectLoading } = useProject(projectId || '')
  const { data: categoryData, isLoading: categoryLoading } = useCategory(category || '')

  const statusStyles = {
    completed: {
      bg: 'bg-accent-emerald/10',
      text: 'text-accent-emerald',
      border: 'border-accent-emerald/20',
      label: t.status.completed,
      icon: '✓'
    },
    'in-progress': {
      bg: 'bg-accent-blue/10',
      text: 'text-accent-blue',
      border: 'border-accent-blue/20',
      label: t.status.inProgress,
      icon: '◐'
    },
    planned: {
      bg: 'bg-accent-purple/10',
      text: 'text-accent-purple',
      border: 'border-accent-purple/20',
      label: t.status.planned,
      icon: '○'
    }
  }

  const projectTags = project ? (Array.isArray(project.tags) ? project.tags : []) : [];

  if (projectLoading || categoryLoading) {
    return <ProjectDetailSkeleton />
  }

  if (!project) {
    return (
      <PageLayout>
        <div className="py-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t.project.notFound}</h1>
          </div>
        </div>
      </PageLayout>
    )
  }

  const status = statusStyles[project.status]
  const tags = Array.isArray(project.tags) ? project.tags : []
  const features = Array.isArray(project.features) ? project.features : []
  const techStack = Array.isArray(project.tech_stack) ? project.tech_stack : []
  const metrics = project.metrics && typeof project.metrics === 'object' ? project.metrics : {}

  const lang = i18n.language as 'en' | 'zh';
  const breadcrumbs = generateBreadcrumbs([
    { label: categoryData?.title || '', path: `/${category}` },
    { label: project.title, path: `/${category}/${projectId}` }
  ], lang);

  return (
    <PageLayout>
      <Meta
        title={`${project.title} | borninsea`}
        description={project.description}
        keywords={projectTags.join(',')}
        image={project.image}
      />
      <StructuredData type="website" breadcrumbs={breadcrumbs} />
      <div className="py-20">
        {/* Hero Image */}
        <div className="relative h-[50vh] lg:h-[60vh] overflow-hidden mb-20">
          <ImageWithFallback
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 -mt-32 relative z-10">
          <div className="max-w-5xl mx-auto">
          </div>
        </div>

        {/* Status Badge */}
        <div className="absolute top-8 right-8">
          <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm ${status.bg} ${status.text} border ${status.border}`}>
            {status.icon} {status.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 -mt-40 relative z-10 pb-20">
        <div className="max-w-5xl mx-auto">
          {/* Main Card */}
          <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-8 lg:p-12 border-b border-border">
              {/* Category */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-muted-foreground">
                  {categoryData?.title}
                </span>
                {project.subtitle && (
                  <>
                    <span className="text-muted-foreground/50">·</span>
                    <span className="text-sm text-muted-foreground">
                      {project.subtitle}
                    </span>
                  </>
                )}
              </div>

              {/* Title */}
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black text-foreground mb-6 leading-tight">
                {project.title}
              </h1>

              {/* Description */}
              <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                {project.description}
              </p>

              {/* Tags */}
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Content Grid */}
            <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-border">
              {/* Main Content */}
              <div className="lg:col-span-2 p-8 lg:p-12">
                {project.content && (
                  <div className="prose prose-lg max-w-none">
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <span className="w-1 h-6 bg-accent-purple rounded-full" />
                      {t.project.details}
                    </h3>
                    <div className="text-foreground/80 leading-relaxed whitespace-pre-line">
                      {project.content}
                    </div>
                  </div>
                )}

                {/* Features */}
                {features.length > 0 && (
                  <div className="mt-10">
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <span className="w-1 h-6 bg-accent-emerald rounded-full" />
                      {t.project.coreFeatures}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl"
                        >
                          <Check className="w-5 h-5 text-accent-emerald flex-shrink-0" />
                          <span className="text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                {project.link && project.link !== '#' && (
                  <div className="mt-10">
                    <Button asChild size="lg" className="w-full sm:w-auto">
                      <a href={project.link} target="_blank" rel="noopener noreferrer">
                        {t.common.visitProject}
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="p-8 lg:p-8 space-y-8 bg-muted/30">
                {/* Metrics */}
                {Object.keys(metrics).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {t.project.projectData}
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(metrics).map(([key, value]) => (
                        <div key={key} className="bg-card p-4 rounded-xl border border-border">
                          <div className="text-2xl font-bold text-foreground">{String(value)}</div>
                          <div className="text-sm text-muted-foreground capitalize">
                            {key.replace(/_/g, ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tech Stack */}
                {techStack.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Wrench className="w-4 h-4" />
                      {t.project.techStack}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Info */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t.project.projectStatus}
                  </h4>
                  <div className={`p-4 rounded-xl ${status.bg} ${status.border} border`}>
                    <div className={`text-lg font-bold ${status.text}`}>
                      {status.icon} {status.label}
                    </div>
                    {project.start_date && (
                      <div className="text-sm text-muted-foreground mt-2">
                        {t.project.startDate}: {new Date(project.start_date).toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US')}
                      </div>
                    )}
                    {project.end_date && (
                      <div className="text-sm text-muted-foreground">
                        {t.project.endDate}: {new Date(project.end_date).toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to Category */}
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg">
              <Link to={category ? `/${category}` : '/'}>
                <ArrowLeft className="mr-2 w-4 h-4" />
                {t.project.viewMore} {categoryData?.title || ''} {t.project.projects}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
